import { ApiError } from '@api/api-client';
import { type EditionResponse, getEditions } from '@api/editions-api';
import {
  type AccessLink,
  deletePmsVoter,
  exportAccessLinks,
  listPmsVoters,
  type PmsVoterDto,
  resetVoterPassword,
} from '@api/pms-voters-api';
import Breadcrumbs from '@components/breadcrumbs';
import Modal from '@components/modal';
import TableFilters from '@components/table-filters';
import ImportPmsVotersModal from '@features/pms-voters/import-pms-csv-modal';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { downloadCsv, escapeCsvField } from '@utils/csv-utils';
import {
  AlertTriangle,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  Contact,
  Copy,
  Download,
  KeyRound,
  RefreshCw,
  Trash2,
  Upload,
  Users,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { sileo } from 'sileo';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Voters of a password-mode site: imported contacts who sign in with an emailed
 * access link and a password, rather than through the marketplace.
 */
export default function ManagePmsVoters(): React.JSX.Element {
  const queryClient = useQueryClient();
  const [editionId, setEditionId] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isImportingVoters, setIsImportingVoters] = useState(false);
  const [resettingVoter, setResettingVoter] = useState<PmsVoterDto | null>(null);
  const [deletingVoter, setDeletingVoter] = useState<PmsVoterDto | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  const { data: editionsResult } = useQuery({
    queryKey: ['editions', 'all-for-pms'],
    queryFn: () => getEditions({ page: 1, pageSize: 100 }),
    staleTime: 60_000,
  });

  // Voters of this kind belong to PMS (password) editions; a marketplace edition's
  // voters come through SSO and are managed elsewhere.
  const pmsEditions = (editionsResult?.data ?? []).filter(
    (e: EditionResponse) => e.authMode === 'password',
  );
  const selectedEdition = pmsEditions.find((e) => e.id === editionId);

  const { data, isLoading, error } = useQuery({
    queryKey: ['pms-voters', currentPage, debouncedSearch, editionId],
    queryFn: () =>
      listPmsVoters(currentPage, PAGE_SIZE, editionId || undefined, debouncedSearch || undefined),
    staleTime: 30_000,
  });

  const voters = data?.data ?? [];
  const totalItems = data?.meta?.totalItems ?? 0;
  const totalPages = data?.meta?.totalPages ?? 1;

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['pms-voters'] });
  }, [queryClient]);

  const handleExport = useCallback(async (): Promise<void> => {
    if (!editionId) return;
    setIsExporting(true);
    try {
      const links = await exportAccessLinks(editionId);

      if (links.length === 0) {
        sileo.warning({ title: 'No access links to export for this edition' });
        return;
      }

      // The first three columns are the importer's, so the file round-trips through
      // HubSpot: the Record ID identifies the contact, and the token goes into the
      // "Awards Voting Token" property it composes the voting link from. The fourth
      // is the link already composed, for whoever writes the campaign email — that
      // is the step where a wrong host or a missing token parameter used to break a
      // whole country's first access.
      const header = [
        'Record ID - Contact',
        'Email',
        'Awards Voting Token [Upload] [Temporary]',
        'Awards Voting URL',
      ]
        .map(escapeCsvField)
        .join(',');
      const rows = links.map((link: AccessLink) =>
        [link.hsid ?? '', link.email, link.token, link.accessUrl].map(escapeCsvField).join(','),
      );
      downloadCsv(
        [header, ...rows].join('\n'),
        `pms-access-tokens-${selectedEdition?.siteCode ?? editionId}.csv`,
      );

      sileo.success({ title: `${links.length} access link(s) exported` });
    } catch (err) {
      sileo.error({ title: err instanceof ApiError ? err.message : 'Export failed' });
    } finally {
      setIsExporting(false);
    }
  }, [editionId, selectedEdition]);

  return (
    <>
      <Breadcrumbs items={[{ label: 'Editions', to: '/editions' }, { label: 'PMS Voters' }]} />

      <div className="page-header">
        <div className="page-header__left">
          <h1 className="page-header__title">PMS Voters</h1>
          <p className="page-header__subtitle">
            {totalItems} voter{totalItems !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="page-header__actions">
          <button type="button" className="btn btn-secondary" onClick={refetch}>
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={isExporting || !editionId}
            data-tooltip={!editionId ? 'Pick an edition first' : undefined}
            onClick={() => void handleExport()}
          >
            <Download size={16} />
            {isExporting ? 'Exporting...' : 'Export access links'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIsImportingVoters(true)}
          >
            <Upload size={16} />
            Import voters
          </button>
        </div>
      </div>

      {/*
        The export is a credential list: each URL signs its holder in. Said plainly
        here because the file carries no protection once it leaves the browser.
      */}
      <div className="callout callout--warning" role="note">
        <AlertTriangle className="callout__icon" size={18} aria-hidden="true" />
        <span>
          <strong className="callout__title">Access links sign a voter in.</strong> Treat the
          exported file like a password list, and only share it with the tool that sends the
          campaign.
        </span>
      </div>

      <TableFilters
        compact
        rows={[
          {
            items: [
              {
                type: 'select',
                id: 'pms-edition',
                label: 'Edition',
                value: editionId,
                onChange: (value: string) => {
                  setEditionId(value);
                  setCurrentPage(1);
                },
                options: [
                  { value: '', label: 'All editions' },
                  ...pmsEditions.map((e) => ({
                    value: e.id,
                    label: `${e.name} · ${e.siteCode}`,
                  })),
                ],
              },
              {
                type: 'search',
                id: 'pms-voter-search',
                label: 'Search',
                placeholder: 'Search by email, name or HubSpot ID...',
                value: search,
                onChange: setSearch,
                narrow: true,
              },
            ],
          },
        ]}
      />

      {isLoading && (
        <div className="empty-state">
          <p>Loading voters...</p>
        </div>
      )}

      {error && (
        <div className="page-error" role="alert">
          <span>{error instanceof Error ? error.message : 'Failed to load voters'}</span>
        </div>
      )}

      {!isLoading && !error && voters.length === 0 && (
        <div className="empty-state">
          <Users size={40} strokeWidth={1} />
          <p>No PMS voters found.</p>
        </div>
      )}

      {!isLoading && !error && voters.length > 0 && (
        <>
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Voter</th>
                  <th>Email</th>
                  <th>HubSpot ID</th>
                  <th>Site</th>
                  <th>Password</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {voters.map((voter) => (
                  <tr key={voter.id}>
                    <td>
                      <div className="user-cell">
                        <span className="user-cell__name">
                          {[voter.name, voter.surname].filter(Boolean).join(' ') || '—'}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className="text-sm">{voter.email}</span>
                    </td>
                    <td>
                      <code className="text-sm">{voter.hsid ?? '—'}</code>
                    </td>
                    <td>
                      <span className="badge badge-draft">{voter.siteCode}</span>
                    </td>
                    <td>
                      {voter.hasPassword ? (
                        <span className="password-status password-status--set">
                          <CheckCircle size={12} />
                          Set
                        </span>
                      ) : (
                        <span className="password-status">Not set</span>
                      )}
                    </td>
                    <td>
                      <div className="table-actions">
                        {voter.hasPassword && (
                          <button
                            type="button"
                            className="btn btn-ghost btn-icon btn-sm"
                            data-tooltip="Reset access"
                            aria-label={`Reset access for ${voter.email}`}
                            onClick={() => setResettingVoter(voter)}
                          >
                            <KeyRound size={16} />
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-ghost btn-icon btn-sm"
                          data-tooltip="Delete voter"
                          aria-label={`Delete voter ${voter.email}`}
                          onClick={() => setDeletingVoter(voter)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <span className="pagination__info">
                Page {currentPage} of {totalPages} &middot; {totalItems} voters
              </span>
              <div className="pagination__controls">
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === 1}
                  aria-label="Previous page"
                  onClick={() => setCurrentPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} />
                </button>
                <button
                  type="button"
                  className="pagination__btn"
                  disabled={currentPage === totalPages}
                  aria-label="Next page"
                  onClick={() => setCurrentPage((p) => p + 1)}
                >
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {isImportingVoters && (
        <ImportPmsVotersModal
          isOpen
          editions={pmsEditions.map((e) => ({ id: e.id, name: e.name, siteCode: e.siteCode }))}
          // The filter below is a convenience, not the target: the modal owns that
          // choice, and only starts from the filter when one is already applied.
          defaultEditionId={editionId}
          onClose={() => setIsImportingVoters(false)}
          onImported={refetch}
        />
      )}

      {resettingVoter && (
        <ResetPasswordModal
          voter={resettingVoter}
          onClose={() => setResettingVoter(null)}
          // Refetch only: the modal stays open to show the generated password,
          // which is the one and only time it can be read.
          onReset={refetch}
        />
      )}

      {deletingVoter && (
        <DeleteVoterModal
          voter={deletingVoter}
          editionName={
            pmsEditions.find((e) => e.id === deletingVoter.editionId)?.name ?? 'this edition'
          }
          onClose={() => setDeletingVoter(null)}
          onDeleted={() => {
            setDeletingVoter(null);
            refetch();
          }}
        />
      )}
    </>
  );
}

interface ResetPasswordModalProps {
  voter: PmsVoterDto;
  onClose: () => void;
  /**
   * Called once the reset succeeds, to refresh the list. It must NOT close this
   * modal: the generated password is shown afterwards and cannot be fetched again.
   */
  onReset: () => void;
}

/**
 * Two steps: confirm, then read the generated password off the screen.
 *
 * This used to clear the password so the voter's original access link asked for a
 * new one, which only helped a voter who still had the link. A generated password
 * is handed over instead, and the voter is required to replace it the first time
 * they sign in with it.
 */
function ResetPasswordModal({
  voter,
  onClose,
  onReset,
}: ResetPasswordModalProps): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [hasCopied, setHasCopied] = useState(false);

  const onConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const { password } = await resetVoterPassword(voter.id);
      setGeneratedPassword(password);
      onReset();
    } catch (err) {
      sileo.error({ title: err instanceof ApiError ? err.message : 'Failed to reset password' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopy = async (): Promise<void> => {
    if (!generatedPassword) return;
    try {
      await navigator.clipboard.writeText(generatedPassword);
      setHasCopied(true);
    } catch {
      sileo.error({ title: 'Could not copy. Select the password and copy it manually.' });
    }
  };

  if (generatedPassword) {
    return (
      <Modal
        isOpen
        onClose={onClose}
        title="Password reset"
        footer={
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Done
          </button>
        }
      >
        <div className="modal-form">
          <p className="form-hint form-hint--spaced">
            Send this password to <strong>{voter.email}</strong>. It is shown only now — closing
            this dialog is the last time you will see it. They will be asked to choose their own
            password the first time they sign in with it.
          </p>

          <div className="form-group">
            <label htmlFor="generated-voter-password" className="form-label">
              Generated password
            </label>
            <div className="password-input-wrapper">
              <input
                id="generated-voter-password"
                type="text"
                className="form-input"
                value={generatedPassword}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
              />
              <button
                type="button"
                className="btn-password-toggle"
                onClick={() => void handleCopy()}
                aria-label="Copy password"
              >
                {hasCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
              </button>
            </div>
            {hasCopied && <span className="form-hint">Copied to the clipboard.</span>}
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Reset password"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={isSubmitting}
            onClick={() => void onConfirm()}
          >
            {isSubmitting ? 'Resetting...' : 'Generate new password'}
          </button>
        </>
      }
    >
      <p>
        Reset the password for <strong>{voter.email}</strong>?
      </p>
      <p className="form-hint form-hint--spaced">
        <Contact size={14} /> This is how a voter who cannot get in is helped back: a new password
        is generated and shown once, for you to send them. Their current password stops working
        immediately, and they will have to choose their own the first time they use the new one.
      </p>
    </Modal>
  );
}

interface DeleteVoterModalProps {
  voter: PmsVoterDto;
  /** The edition the row belongs to, named so the scope of the removal is clear. */
  editionName: string;
  onClose: () => void;
  onDeleted: () => void;
}

/**
 * The counterpart of the import, one row at a time: a contact who should never
 * have been in the census, a duplicate, or someone who asked to be left out.
 *
 * The dialog spells out the two things that are easy to get wrong about it — the
 * votes go too, and the removal is of one edition, not of the person.
 */
function DeleteVoterModal({
  voter,
  editionName,
  onClose,
  onDeleted,
}: DeleteVoterModalProps): React.JSX.Element {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const voterName = [voter.name, voter.surname].filter(Boolean).join(' ') || voter.email;

  const onConfirm = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      const { deletedVotes } = await deletePmsVoter(voter.id);
      sileo.success({
        title:
          deletedVotes > 0
            ? `${voter.email} removed, along with ${deletedVotes} vote(s)`
            : `${voter.email} removed from the census`,
      });
      onDeleted();
    } catch (err) {
      sileo.error({ title: err instanceof ApiError ? err.message : 'Failed to delete voter' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Delete voter"
      footer={
        <>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={isSubmitting}
            onClick={() => void onConfirm()}
          >
            {isSubmitting ? 'Deleting...' : 'Delete voter'}
          </button>
        </>
      }
    >
      <p>
        Remove <strong>{voterName}</strong> from the census of <strong>{editionName}</strong>?
      </p>

      {voter.voteCount > 0 && (
        <div className="callout callout--warning" role="note">
          <AlertTriangle className="callout__icon" size={18} aria-hidden="true" />
          <span>
            <strong className="callout__title">
              {voter.voteCount} vote{voter.voteCount !== 1 ? 's' : ''} will be deleted too.
            </strong>{' '}
            The nominees they voted for lose those votes, so the results of this edition change.
          </span>
        </div>
      )}

      <p className="form-hint form-hint--spaced">
        Their access link for this edition stops working. Enrollments in other editions are not
        touched, and if this was the only one, their account and password go with it. Importing them
        again brings the voter back.
      </p>
    </Modal>
  );
}
